// PR-I-84: helpers de formato compartidos entre variantes de BookingCard.
//
// Regla de precio (Armando 2026-09-09): moneda al frente, sin `$`, coma en
// miles, sin decimales. Ejemplos:
//   USD 2,000/Guest
//   USD 30/hour
//   USD 11,200 total
//
// Reciben strings (el back emite decimales como string) o numbers.

const DEFAULT_CURRENCY = 'USD'

/**
 * Parsea un decimal serializado como string o number a integer (sin decimales).
 * Retorna null si no se puede interpretar. Redondea al entero mas cercano.
 */
const _toInteger = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = typeof value === 'number' ? value : Number.parseFloat(value)
  if (!Number.isFinite(num)) return null
  return Math.round(num)
}

/**
 * Devuelve "USD 2,000" para amount=2000. Sin decimales, con coma en miles.
 * Retorna null si amount no es interpretable como numero.
 */
export const formatMoney = (
  amount: string | number | null | undefined,
  currency: string | null | undefined = DEFAULT_CURRENCY,
): string | null => {
  const intAmount = _toInteger(amount)
  if (intAmount === null) return null
  const currencyLabel = (currency || DEFAULT_CURRENCY).toUpperCase()
  return `${currencyLabel} ${intAmount.toLocaleString('en-US')}`
}

/**
 * Sufijo humano para cada unidad de precio esperada por la card.
 * `hour` -> "/hour" ; `guest` -> "/Guest" ; `total` -> " total" ; null/undefined -> "".
 */
export const priceUnitSuffix = (unit: 'hour' | 'guest' | 'total' | null | undefined): string => {
  switch (unit) {
    case 'hour':
      return '/hour'
    case 'guest':
      return '/Guest'
    case 'total':
      return ' total'
    default:
      return ''
  }
}

/**
 * Formato compuesto: moneda + sufijo. Retorna null si el amount no es valido
 * para no dejar la card mostrando "USD null/Guest".
 */
export const formatPriceWithUnit = (
  amount: string | number | null | undefined,
  currency: string | null | undefined,
  unit: 'hour' | 'guest' | 'total' | null | undefined,
): string | null => {
  const money = formatMoney(amount, currency)
  if (!money) return null
  return `${money}${priceUnitSuffix(unit)}`
}

// ---------------------------------------------------------------------------
// Duracion: `duration_type` viene como "hours" | "days" | "weeks" (plural).
// El back no siempre normaliza, asi que aceptamos singular tambien.
// ---------------------------------------------------------------------------

const _DURATION_LABELS: Record<string, [string, string]> = {
  // key -> [singular, plural]
  hour: ['Hour', 'Hours'],
  hours: ['Hour', 'Hours'],
  day: ['Day', 'Days'],
  days: ['Day', 'Days'],
  week: ['Week', 'Weeks'],
  weeks: ['Week', 'Weeks'],
  month: ['Month', 'Months'],
  months: ['Month', 'Months'],
}

/**
 * Devuelve "7 Hours" / "1 Day" / "2 Weeks" respetando singular/plural.
 * Si no reconoce el tipo, capitaliza la unidad recibida como fallback.
 * Retorna null si duration no es un numero interpretable.
 */
export const formatDuration = (
  duration: string | number | null | undefined,
  durationType: string | null | undefined,
): string | null => {
  const num = _toInteger(duration)
  if (num === null) return null
  const key = (durationType || 'hour').toLowerCase()
  const [singular, plural] = _DURATION_LABELS[key] || [
    key.charAt(0).toUpperCase() + key.slice(1),
    key.charAt(0).toUpperCase() + key.slice(1),
  ]
  return `${num} ${num === 1 ? singular : plural}`
}

// ---------------------------------------------------------------------------
// Audience type: convencion del app (ver DetailExperienceDrawer.tsx:108).
//   audienceType === "2"  -> "Only Adults"
//   cualquier otro         -> "Family Friendly"
// Si no viene el campo, retornamos null para que la card no lo muestre.
// ---------------------------------------------------------------------------

export const audienceLabel = (audienceType: string | null | undefined): string | null => {
  if (audienceType === null || audienceType === undefined || audienceType === '') return null
  return Number(audienceType) === 2 ? 'Only Adults' : 'Family Friendly'
}
